import React, { useEffect, useState, useRef } from 'react';
import { Modal } from '@/components';
import { ModalProps } from '@mui/base/Modal';
import { IPOIData } from '@/types/poi-management.types';
import clsx from 'clsx';
import { I18N_LANGUAGES } from '@/i18n';
import { useIntl } from 'react-intl';
import { useTranslatePOIContentMutation } from '@/services/POIManagementService';
import { useSnackbar } from 'notistack';
import { formatFinnishCoordinate } from '@/utils';

interface PreviewModalProps extends Omit<ModalProps, 'children'> {
  formData: Partial<IPOIData>;
  source_language?: string;
  onTranslationsGenerated?: (
    translations: Record<string, Partial<IPOIData>>
  ) => void;
  harborId?: string | null;
}

interface LanguagePreviewProps {
  language: {
    code: string;
    label: string;
    flag: string;
  };
  formData: Partial<IPOIData>;
  translatedContent?: Record<
    string,
    {
      name?: string;
      description?: string;
      category?: string;
      tags?: string[];
    }
  >;
  isLoading: boolean;
  onEditContent?: (
    languageCode: string,
    field: keyof EditValues,
    value: string | string[]
  ) => void;
  isEditable?: boolean;
}

// Interface for translation payload
interface TranslationPayload {
  name?: string;
  category_id?: string;
  category?: string;
  description?: string;
  tags?: string[];
  source_language: string;
  poi_id?: string;
  harborId?: string | null;
}

// Interface for edit values
interface EditValues {
  name?: string;
  category?: string;
  description?: string;
  tags?: string[];
}

const FIELD_LABELS = {
  en: {
    name: 'Name',
    category: 'Category',
    description: 'Description',
    tags: 'Tags',
  },
  de: {
    name: 'Name',
    category: 'Kategorie',
    description: 'Beschreibung',
    tags: 'Tags',
  },
  fi: {
    name: 'Nimi',
    category: 'Kategoria',
    description: 'Kuvaus',
    tags: 'Tunnisteet',
  },
  sv: {
    name: 'Namn',
    category: 'Kategori',
    description: 'Beskrivning',
    tags: 'Taggar',
  },
} as const;

const LanguagePreview: React.FC<LanguagePreviewProps> = ({
  language,
  formData,
  translatedContent,
  isLoading,
  onEditContent,
  isEditable = true,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editValues, setEditValues] = useState<EditValues>({});

  const fieldLabels =
    FIELD_LABELS[language.code as keyof typeof FIELD_LABELS] || FIELD_LABELS.en;

  const langContent =
    translatedContent && translatedContent[language.code]
      ? translatedContent[language.code]
      : formData;

  const toggleEditMode = () => {
    if (!isEditable) return;
    if (!isEditing) {
      const initialValues: EditValues = {};
      if (langContent?.name) initialValues.name = langContent.name;
      if (langContent?.description)
        initialValues.description = langContent.description;
      if (langContent?.tags) initialValues.tags = [...langContent.tags];
      setEditValues(initialValues);
    }
    setIsEditing(!isEditing);
  };

  const handleSave = () => {
    if (onEditContent) {
      Object.entries(editValues).forEach(([field, value]) => {
        onEditContent(language.code, field as keyof EditValues, value);
      });
      setIsEditing(false);
    }
  };

  const handleChange = (field: keyof EditValues, value: string | string[]) => {
    setEditValues((prev) => ({ ...prev, [field]: value }));
  };

  // Handle tag editing
  const handleTagChange = (index: number, value: string) => {
    if (editValues.tags && Array.isArray(editValues.tags)) {
      const updatedTags = [...editValues.tags];
      updatedTags[index] = value;
      setEditValues((prev) => ({ ...prev, tags: updatedTags }));
    }
  };

  useEffect(() => {
    if (langContent) {
      const initialValues: EditValues = {};
      if (langContent.name) initialValues.name = langContent.name;
      if (langContent.description)
        initialValues.description = langContent.description;
      if (langContent.tags && langContent.tags.length > 0)
        initialValues.tags = [...langContent.tags];
      setEditValues(initialValues);
    }
  }, [langContent]);

  return (
    <div className="bg-gray-100 shadow-sm hover:shadow-md transition-all duration-300 rounded-xl overflow-hidden mb-5 border border-gray-200">
      <div className="bg-gray-200 px-5 py-3 flex items-center justify-between border-b border-gray-100">
        <div className="flex items-center">
          <img
            src={language.flag}
            alt={language.label}
            className="w-6 h-6 mr-3 rounded-sm shadow-sm"
          />
          <h3 className="text-base font-medium text-gray-800 flex items-center">
            {language.label}
            {isLoading && (
              <span className="ml-2 flex items-center text-xs text-blue-600 contrast:text-blue-200 font-medium">
                <svg className="animate-spin h-3 w-3 mr-1" viewBox="0 0 24 24">
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                    fill="none"
                  ></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
                translating...
              </span>
            )}
          </h3>
        </div>

        {/* Single Edit Icon for all fields */}
        {!isLoading && onEditContent && isEditable && (
          <button
            onClick={toggleEditMode}
            className={`${isEditing
                ? 'bg-blue-100 dark:bg-blue-900'
                : 'bg-gray-100 dark:bg-gray-400'
              } text-blue-600 dark:text-blue-200 hover:text-blue-800 p-1.5 rounded-full hover:bg-blue-50 dark:hover:bg-blue-600 transition-colors`}
            title={isEditing ? 'Cancel editing' : 'Edit all fields'}
          >
            <svg
              className="w-4 h-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"
              />
            </svg>
          </button>
        )}
      </div>

      {isLoading ? (
        <div className="p-5 flex justify-center">
          <div className="animate-pulse flex space-x-4 w-full">
            <div className="flex-1 space-y-4 py-1">
              <div className="h-4 bg-gray-200 rounded w-3/4"></div>
              <div className="space-y-2">
                <div className="h-4 bg-gray-200 rounded"></div>
                <div className="h-4 bg-gray-200 rounded w-5/6"></div>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="p-5">
          {isEditing ? (
            <>
              {/* Edit Mode View */}
              <div className="space-y-6">
                {/* Name Field */}
                <div className="col-span-1">
                  <p className="text-xs uppercase tracking-wider font-medium text-gray-500 mb-1">
                    Name
                  </p>
                  <input
                    type="text"
                    value={editValues.name || ''}
                    onChange={(e) => handleChange('name', e.target.value)}
                    className="w-full input p-2 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 text-base"
                    placeholder="Enter name"
                    autoComplete="off"
                  />
                </div>

                {/* Category Field (read-only in preview) */}
                <div className="col-span-1">
                  <p className="text-xs uppercase tracking-wider font-medium text-gray-500 mb-1">
                    {fieldLabels.category}
                  </p>
                  <p className="text-base font-medium text-gray-800">
                    {langContent?.category || '-'}
                  </p>
                </div>

                {/* Description Field */}
                <div className="col-span-2">
                  <p className="text-xs uppercase tracking-wider font-medium text-gray-500 mb-1">
                    {fieldLabels.description}
                  </p>
                  <textarea
                    value={editValues.description || ''}
                    onChange={(e) =>
                      handleChange('description', e.target.value)
                    }
                    className="w-full textarea p-2 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 text-base min-h-[100px]"
                    placeholder="Enter description"
                  />
                </div>

                {/* Tags Field */}
                <div className="col-span-2">
                  <p className="text-xs uppercase tracking-wider font-medium text-gray-500 mb-1">
                    {fieldLabels.tags}
                  </p>
                  <div className="flex flex-wrap gap-2 mb-2">
                    {Array.isArray(editValues.tags) &&
                      editValues.tags.map((tag, index) => (
                        <div key={index} className="flex items-center">
                          <label
                            htmlFor={`poi-tag-${index}`}
                            className="sr-only"
                          >
                            Tag {index + 1}
                          </label>
                          <input
                            id={`poi-tag-${index}`}
                            type="text"
                            value={tag}
                            onChange={(e) =>
                              handleTagChange(index, e.target.value)
                            }
                            className="p-2 input rounded focus:outline-none focus:ring-1 focus:ring-blue-500 text-sm"
                          />
                        </div>
                      ))}
                  </div>
                </div>

                {/* Save Button */}
                <div className="flex justify-end mt-4">
                  <button
                    onClick={handleSave}
                    className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 flex items-center"
                    title="Save all changes"
                  >
                    <svg
                      className="w-4 h-4 mr-2"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                    Save Changes
                  </button>
                </div>
              </div>
            </>
          ) : (
            <>
              {/* Normal View Mode */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Name Field */}
                <div className="col-span-1">
                  <p className="text-xs uppercase tracking-wider font-medium text-gray-500 mb-1">
                    {fieldLabels.name}
                  </p>
                  <p className="text-base font-medium text-gray-800">
                    {langContent?.name || '-'}
                  </p>
                </div>

                {/* Category Field */}
                <div className="col-span-1">
                  <p className="text-xs uppercase tracking-wider font-medium text-gray-500 mb-1">
                    {fieldLabels.category}
                  </p>
                  <p className="text-base font-medium text-gray-800">
                    {langContent?.category || '-'}
                  </p>
                </div>

                {/* Description Field */}
                <div className="col-span-2">
                  <p className="text-xs uppercase tracking-wider font-medium text-gray-500 mb-1">
                    {fieldLabels.description}
                  </p>
                  <p className="text-base text-gray-700 whitespace-pre-wrap leading-relaxed">
                    {langContent?.description || '-'}
                  </p>
                </div>

                {/* Tags Field */}
                {langContent?.tags && langContent.tags.length > 0 && (
                  <div className="col-span-2">
                    <p className="text-xs uppercase tracking-wider font-medium text-gray-500 mb-2">
                      {fieldLabels.tags}
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {langContent.tags.map((tag) => (
                        <span
                          key={tag}
                          className="bg-blue-50 dark:bg-blue-900 text-blue-700 dark:text-blue-200 text-xs font-medium px-3 py-1 rounded-full"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

const PreviewModal: React.FC<PreviewModalProps> = ({
  formData,
  source_language,
  open,
  onClose,
  className,
  onTranslationsGenerated,
  harborId,
}) => {
  const intl = useIntl();
  const { enqueueSnackbar } = useSnackbar();
  const [translatePOIContent, { isLoading }] = useTranslatePOIContentMutation();
  const [translations, setTranslations] = useState<
    Record<
      string,
      {
        name?: string;
        description?: string;
        category?: string;
        tags?: string[];
      }
    >
  >({});
  const [hasTranslated, setHasTranslated] = useState(false);
  const [lastFormData, setLastFormData] = useState<string>('');

  const [contentModified, setContentModified] = useState(false);

  const translatingRef = useRef(false);
  const onTranslationsGeneratedRef = useRef(onTranslationsGenerated);

  useEffect(() => {
    onTranslationsGeneratedRef.current = onTranslationsGenerated;
  }, [onTranslationsGenerated]);

  useEffect(() => {
    if (open) {
      setContentModified(false);
    }
  }, [open]);

  useEffect(() => {
    const currentFormDataString = JSON.stringify({
      name: formData.name,
      category: formData.category,
      description: formData.description,
      tags: formData.tags,
    });

    if (lastFormData && lastFormData !== currentFormDataString) {
      setHasTranslated(false);
      setContentModified(true);
      setLastFormData(currentFormDataString);
    } else if (!lastFormData) {
      setLastFormData(currentFormDataString);
      setContentModified(false);
    }
  }, [formData, lastFormData]);

  const handleEditContent = (
    languageCode: string,
    field: keyof EditValues,
    value: string | string[]
  ) => {
    setTranslations((prev) => {
      const newTranslations = { ...prev };

      if (!newTranslations[languageCode]) {
        newTranslations[languageCode] = {};
      }

      newTranslations[languageCode] = {
        ...newTranslations[languageCode],
        [field]: value,
      };

      if (onTranslationsGeneratedRef.current) {
        onTranslationsGeneratedRef.current(newTranslations);
      }

      return newTranslations;
    });
  };

  useEffect(() => {
    const fetchTranslations = async () => {
      if (!open || hasTranslated || translatingRef.current) return;

      try {
        translatingRef.current = true;
        const dataToTranslate: TranslationPayload = {
          name: formData.name,
          category_id: formData.category_id
            ? String(formData.category_id)
            : undefined,
          description: formData.description,
          tags: formData.tags,
          source_language: source_language || 'en',
          harborId: harborId || null,
          ...(formData.id && !contentModified ? { poi_id: formData.id } : {}),
        };

        const response = await translatePOIContent(dataToTranslate).unwrap();
        if (response.warning_message) {
          enqueueSnackbar(response.warning_message, {
            variant: 'warning',
          });
        }

        if (response.translations) {
          let finalTranslations = response.translations;

          if (response.warning_message) {
            const sourceLang = source_language || 'en';
            const sourceTranslation =
              response.translations[sourceLang] ||
              response.translations[Object.keys(response.translations)[0]];

            finalTranslations = { ...response.translations };
            I18N_LANGUAGES.forEach((lang) => {
              if (!finalTranslations[lang.code]) {
                finalTranslations[lang.code] = {
                  name: sourceTranslation?.name || formData.name || '',
                  description:
                    sourceTranslation?.description ||
                    formData.description ||
                    '',
                  category:
                    sourceTranslation?.category || formData.category || '',
                  tags: sourceTranslation?.tags
                    ? [...sourceTranslation.tags]
                    : formData.tags
                      ? [...formData.tags]
                      : [],
                };
              }
            });
          }

          setTranslations(finalTranslations);
          setHasTranslated(true);

          if (onTranslationsGeneratedRef.current) {
            onTranslationsGeneratedRef.current(finalTranslations);
          }
        }
      } catch (error) {
        console.error('Translation failed:', error);
        enqueueSnackbar(
          intl.formatMessage({ id: 'POI.PREVIEW.TRANSLATION_ERROR' }),
          {
            variant: 'error',
          }
        );
      } finally {
        translatingRef.current = false;
      }
    };

    fetchTranslations();
  }, [
    open,
    formData,
    translatePOIContent,
    enqueueSnackbar,
    intl,
    hasTranslated,
    contentModified,
    source_language,
    harborId,
  ]);

  return (
    <Modal
      open={open}
      onClose={onClose}
      className={clsx(
        'harbor-preview-root !flex items-center justify-center',
        className
      )}
      aria-labelledby="preview-modal-title"
      aria-describedby="preview-modal-description"
    >
      <div className="harbor-preview-modal bg-gray-100 w-full max-w-4xl mx-4 rounded-xl shadow-xl flex flex-col overflow-y-auto max-h-[560px] custom-scrollbar">
        {/* Header */}
        <div className="modal-header bg-gray-200 px-6 py-4 border-b border-gray-100">
          <div className="modal-header-row flex justify-between items-center w-full">
            <h1
              id="preview-modal-title"
              className="text-xl font-bold text-gray-800 flex items-center"
            >
              <svg
                className="w-5 h-5 mr-2 text-blue-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                />
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                />
              </svg>
              {intl.formatMessage({ id: 'POI.PREVIEW.TITLE' })}
            </h1>
            <button
              type="button"
              onClick={() => onClose?.({}, 'backdropClick')}
              className="modal-close text-gray-400 hover:text-gray-600 focus:outline-none p-1 rounded-full hover:bg-gray-100 transition-colors"
              aria-label={intl.formatMessage({ id: 'POI.BUTTON.CLOSE' })}
            >
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>
        </div>

        <div className="p-6">
          {/* Location and Images Section */}
          <div className="bg-blue-50 dark:bg-blue-950 contrast:bg-blue-950 rounded-xl p-4 mb-6 flex flex-wrap">
            <div className="flex-1 min-w-[200px]">
              <div className="flex items-center mb-3">
                <svg
                  className="w-5 h-5 text-blue-600 mr-2"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                  />
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                  />
                </svg>
                <h2 className="font-semibold text-gray-800">Location</h2>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {intl.formatMessage({ id: 'POI.FORM.LATITUDE' })}
                  </p>
                  <p className="text-base font-medium text-gray-800 mt-1">
                    {formData.latitude !== undefined &&
                      formData.latitude !== null
                      ? formatFinnishCoordinate(formData.latitude)
                      : '-'}
                  </p>
                </div>
                <div>
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {intl.formatMessage({ id: 'POI.FORM.LONGITUDE' })}
                  </p>
                  <p className="text-base font-medium text-gray-800 mt-1">
                    {formData.longitude !== undefined &&
                      formData.longitude !== null
                      ? formatFinnishCoordinate(formData.longitude)
                      : '-'}
                  </p>
                </div>
              </div>
            </div>

            {/* Images gallery */}
            {formData.images && formData.images.length > 0 && (
              <div className="flex gap-2 items-center ml-auto mt-3 sm:mt-0">
                {formData.images.slice(0, 3).map((image, index) => {
                  const src =
                    image instanceof File ? URL.createObjectURL(image) : image;
                  return (
                    <div
                      key={index}
                      className="w-16 h-16 rounded-md overflow-hidden shadow-sm"
                    >
                      <img
                        src={src}
                        alt={`Preview ${index + 1}`}
                        className="w-full h-full object-cover transition-transform hover:scale-110"
                      />
                    </div>
                  );
                })}
                {formData.images.length > 3 && (
                  <div className="w-16 h-16 bg-blue-100 dark:bg-blue-900 contrast:bg-blue-900 rounded-md shadow-sm flex items-center justify-center">
                    <span className="text-sm font-medium text-blue-700 dark:text-blue-100 contrast:text-blue-100">
                      +{formData.images.length - 3}
                    </span>
                  </div>
                )}
              </div>
            )}
          </div>

          <p className="text-sm text-gray-500 mb-4 flex items-center">
            <svg
              className="w-4 h-4 mr-1"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            {intl.formatMessage({ id: 'POI.PREVIEW.DESCRIPTION' })}
          </p>

          <div className="lang-list pr-2 custom-scrollbar">
            {I18N_LANGUAGES.map((lang) => (
              <LanguagePreview
                key={lang.code}
                language={lang}
                formData={formData}
                translatedContent={translations}
                isLoading={isLoading}
                onEditContent={handleEditContent}
                isEditable={lang.code !== (source_language || 'en')}
              />
            ))}
          </div>
        </div>

        <div className="px-6 py-4 bg-gray-200 border-t border-gray-100">
          <div className="footer-actions-row flex justify-end">
            <button
              type="button"
              onClick={() => onClose?.({}, 'backdropClick')}
              className="inline-flex items-center px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 shadow-sm transition-colors duration-200"
              aria-label={intl.formatMessage({ id: 'POI.BUTTON.CLOSE' })}
            >
              {intl.formatMessage({ id: 'POI.BUTTON.CLOSE' })}
            </button>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default PreviewModal;
